"use client";

import Image from "next/image";
import { useMemo, useSyncExternalStore } from "react";
import Link from "next/link";

import { NAVBAR_LINKS, NAVBAR_ACTIONS, SUB_DRAWERS } from "./navbarData";
import type { SubDrawer } from "./navbarData";
import DesktopNavbar from "./desktop/DesktopNavbar";
import MobileNavbar from "./mobile/MobileNavbar";
import useNavbar from "./useNavbar";
import { useFormationsDrawer } from "./useFormationsDrawer";

function subscribeNoop() {
  return () => {};
}

function getMountedSnapshot() {
  return true;
}

function getMountedServerSnapshot() {
  return false;
}

export default function Navbar() {
  const mounted = useSyncExternalStore(subscribeNoop, getMountedSnapshot, getMountedServerSnapshot);
  const navbarState = useNavbar();
  const { isMobile } = navbarState;

  // Fetch real formation categories once — shared by desktop + mobile
  const { drawer: liveFormationsDrawer, subDrawers: liveCatSubDrawers } =
    useFormationsDrawer();

  // Merge live data into the static drawer map.
  // While the API is loading, liveFormationsDrawer is null and we fall back to
  // the hardcoded SUB_DRAWERS['formations'] so the menu is never empty.
  const subDrawers = useMemo<Record<string, SubDrawer>>(() => {
    if (!liveFormationsDrawer) return SUB_DRAWERS; // fallback while loading
    return {
      ...SUB_DRAWERS,
      formations: liveFormationsDrawer, // replace static with live data
      ...liveCatSubDrawers,             // per-category sub-drawers for mobile
    };
  }, [liveFormationsDrawer, liveCatSubDrawers]);

  // Prevent hydration mismatch — render the static desktop nav on the server
  if (!mounted) {
    return (
      <nav className="navbar px-4 xl:px-11 absolute w-full h-auto left-0 md:top-[25px] top-4 flex flex-row justify-between items-center gap-4 isolate z-[100] md:flex-row md:items-start md:justify-between md:gap-0">
        <Link href="/">
          <Image
            src="/assets/logo.svg"
            alt="Spot Formations"
            title="Spot Formations"
            width={258}
            height={38}
            className="navbar-logo h-auto"
          />
        </Link>
        <DesktopNavbar links={NAVBAR_LINKS} subDrawers={SUB_DRAWERS} />
      </nav>
    );
  }

  return (
    <nav data-print-hide className="navbar px-4 xl:px-11 absolute w-full md:w-[100%] h-auto left-0 flex flex-row justify-between items-center isolate z-[100] gap-4 md:flex-row md:items-center md:justify-between lg:top-6.25 md:top-6.25 top-3">
      <Link href="/">
        <Image
          src="/assets/logo.svg"
          alt="Spot Formations"
          title="Spot Formations"
          width={258}
          height={38}
          className="navbar-logo h-auto"
        />
      </Link>

      {isMobile ? (
        <MobileNavbar
          links={NAVBAR_LINKS}
          actions={NAVBAR_ACTIONS}
          subDrawers={subDrawers}
          {...navbarState}
        />
      ) : (
        <DesktopNavbar links={NAVBAR_LINKS} subDrawers={subDrawers} />
      )}
    </nav>
  );
}
